import React, { useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './ProfileDropdown.css';

interface ProfileDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (page: string) => void;
  darkMode?: boolean;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ 
  isOpen, 
  onClose, 
  onNavigate, 
  darkMode = false 
}) => {
  const { user, logout } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !user) return null;

  const handleLogout = async () => {
    await logout();
    onClose();
  };

  const handleNavigation = (page: string) => {
    onNavigate(page);
    onClose();
  };

  return (
    <div className={`profile-dropdown-wrapper ${darkMode ? 'dark' : 'light'}`} ref={dropdownRef}>
      <div className="profile-dropdown">
        <div className="profile-header">
          <div className="profile-info">
            <p className="profile-name">{user.name}</p>
            <p className="profile-email">{user.email}</p>
          </div>
        </div>
        <div className="profile-menu">
          <button 
            className="profile-menu-item"
            onClick={() => handleNavigation('profile')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 8a3 3 0 100-6 3 3 0 000 6zM14 14v-1a3 3 0 00-3-3H5a3 3 0 00-3 3v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Profile
          </button>
          <button 
            className="profile-menu-item"
            onClick={() => handleNavigation('resume')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.333 2H10a2 2 0 00-2 2v10a2 2 0 002 2h3.333A1.333 1.333 0 0014.667 14.667V3.333A1.333 1.333 0 0013.333 2zM6 2H2.667A1.333 1.333 0 001.333 3.333v11.334A1.333 1.333 0 002.667 16H6a2 2 0 002-2V4a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Resume
          </button>
          <button 
            className="profile-menu-item"
            onClick={() => handleNavigation('settings')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 10a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12.933 10a1.1 1.1 0 00.22-.215l1.355-1.355a1.467 1.467 0 10-2.074-2.074l-1.355 1.355a1.1 1.1 0 00-.215.22m4.069 2.069a5.733 5.733 0 01-1.474 2.516 5.333 5.333 0 01-7.925 0 5.333 5.333 0 010-7.925 5.733 5.733 0 012.516-1.474" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Settings
          </button>
          <button 
            className="profile-menu-item"
            onClick={() => handleNavigation('referrals')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 8h6M13 5v6M6 14a6 6 0 100-12 6 6 0 000 12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            My Referrals
          </button>
          <div className="profile-menu-divider"></div>
          <button className="profile-menu-item" onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 14H3.333A1.333 1.333 0 012 12.667V3.333A1.333 1.333 0 013.333 2H6M10.667 11.333L14 8m0 0l-3.333-3.333M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileDropdown;